<script>
import { required } from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import Drawer from "@/components/drawer";
import VLazyImage from "v-lazy-image/v2";
import draggable from "vuedraggable";

// Custom Components
import PageHeader from "@/components/page-header";
import RoomDetail from "@/components/hotel/ourRoom.vue";
import hotelService from "@/helpers/APIService/hotel.service";
import errorHandler from "@/helpers/errorHandler";
import Middleware from "@/helpers/middlewares";
import { authComputed } from "@/state/helpers";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Room",
    meta: [
      {
        name: "description",
        content: appConfig.description,
      },
    ],
  },
  components: {
    PageHeader,
    Drawer,
    RoomDetail,
    VLazyImage,
    draggable,
  },
  props: {
    currentProps: Object,
  },
  data() {
    return {
      editable: true,
      isDragging: false,
      title: "Room Info And Services",
      table: {
        tableData: [],
      },
      openRoomDetailDrawer: false,
      selectedRoomDetail: null,
      isEdit: false,
      isDataLoading: true,
    };
  },
  computed: {
    ...authComputed,
    dragOptions() {
      return {
        animation: 0,
        group: "description",
        disabled: !this.editable,
        ghostClass: "ghost",
      };
    },
    RoomDetailProps() {
      return {
        selectedRoomDetail: this.selectedRoomDetail,
        isEdit: this.isEdit,
      };
    },
  },
  validations: {
    infoServices_images: {
      required,
    },
  },
  mounted() {
    this.getRoomDetailData();
    this.$root.$on("updateImageStatusInRoom", (data) => {
      this.manageImageResponse(data, "updateImageStatusInRoom");
    });
    this.$root.$on("imageAddedInRoom", (data) => {
      this.manageImageResponse(data, "imageAddedInRoom");
    });
    this.$root.$on("removeImageInRoom", (data) => {
      this.manageImageResponse(data, "removeImageInRoom");
    });
  },
  methods: {
    // Method is use for get booking list data
    async getRoomDetailData() {
      this.isDataLoading = true;
      await hotelService
        .getInfoAndServices("?info_place_type=room")
        .then((response) => {
          this.table.tableData = response;
        })
        .catch((error) => {
          errorHandler(error);
        });
      this.isDataLoading = false;
    },
    // Method is use for change status
    changeStatus(id, data) {
      hotelService
        .updateInfoAndServices(id, data)
        .then((roomDetail) => {
          this.$emit("submitted", roomDetail);
          this.submitted = false;
          Middleware.notification("Status changed successfully !!", "success");
        })
        .catch((error) => {
          errorHandler(error);
        });
    },
    // Method is use for open and close drawer
    RoomDetailForm() {
      if (this.openRoomDetailDrawer == true) {
        this.selectedRoomDetail = null;
        this.isEdit = false;
      }
      this.openRoomDetailDrawer = !this.openRoomDetailDrawer;
    },
    // Method is use for update live data
    RoomDetailAddedUpdated(RoomDetail) {
      if (this.isEdit == true) {
        this.table.tableData = this.table.tableData.map((item) => {
          if (item.id === RoomDetail.id) {
            item = RoomDetail;
          }
          return item;
        });
        Middleware.notification("Successfully updated !!", "success");
      } else {
        this.table.tableData.unshift(RoomDetail);
        Middleware.notification("Successfully added !!", "success");
      }
      this.RoomDetailForm();
    },
    saveAndAddNewRoomDetail(RoomDetail) {
      this.table.tableData.push(RoomDetail);
      Middleware.notification("Successfully added !!", "success");
    },
    // Method is use for update info and services based on ID
    edit(data) {
      this.selectedRoomDetail = data;
      this.isEdit = true;
      this.RoomDetailForm();
    },
    // Method is use for delete info and service
    deleteInfoServices(id, index) {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#238dfa",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Yes, delete it!",
      }).then((result) => {
        if (result.value) {
          hotelService
            .deleteInfoAndServices(id)
            .then(() => {
              this.table.tableData.splice(index, 1);
              Middleware.notification("Successfully deleted !!", "success");
            })
            .catch((error) => {
              errorHandler(error);
            });
        }
      });
    },
    // Method is used to manage image update response
    manageImageResponse(data, emit) {
      this.table.tableData = this.table.tableData.map((item) => {
        if (item.id == data.hotel_info_place) {
          if (emit == "updateImageStatusInRoom") {
            item.hotel_info_place_image = item.hotel_info_place_image.map(
              (img) => {
                if (img.id == data.id) {
                  img.is_thumbnail = true;
                } else {
                  img.is_thumbnail = false;
                }
                return img;
              }
            );
          }
          if (emit == "removeImageInRoom") {
            item.hotel_info_place_image = item.hotel_info_place_image.filter(
              (img) => img.id !== data.id
            );
          }
          if (emit == "imageAddedInRoom") {
            item.hotel_info_place_image.push(data);
          }
        }
        return item;
      });
    },
    // Method is use for change position change
    onPositionChange: async function ($evt) {
      try {
        if ($evt.moved.element == undefined) {
          Middleware.notification("You can't move this one !!", "info");
        } else {
          let infoAndServiceData = new FormData();
          infoAndServiceData.append("priority_order", $evt.moved.newIndex + 1);
          await hotelService.updateInfoAndServices(
            $evt.moved.element.id,
            infoAndServiceData
          );
          Middleware.notification("Positions changed !!", "success");
        }
      } catch (error) {
        errorHandler(error);
      }
    },
  },
};
</script>

<template>
  <div>
    <PageHeader
      :title="title"
      :action="RoomDetailForm"
      text="Add New Room Info"
      :isButton="true"
    />
    <div class="row">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-body">
            <!-- Table -->
            <div class="table-responsive mb-0" v-dragscroll>
              <table
                class="table table-hover mb-0 common-table"
                ref="my-table"
                infinite-scroll-disabled="busy"
                infinite-scroll-distance="10"
              >
                <thead class="table-light">
                  <tr>
                    <th>Name</th>
                    <th>Slug Name</th>
                    <th>Active</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody v-if="isDataLoading">
                  <tr v-for="number in 20" :key="number">
                    <td class="placeholder-glow" v-for="no in 4" :key="no">
                      <span class="placeholder w-100 placeholder-lg"></span>
                    </td>
                  </tr>
                </tbody>
                <draggable
                  v-else
                  v-model="table.tableData"
                  v-bind="dragOptions"
                  @change="onPositionChange"
                  tag="tbody"
                  id="infinite-list"
                >
                  <tr v-for="(data, index) in table.tableData" :key="index">
                    <td @click="edit(data)">
                      <div class="d-flex align-items-center">
                        <img
                          v-if="data.hotel_info_place_image.length == 0"
                          key="image"
                          src="../../../assets/images/placeholder.png"
                          alt=""
                          class="rounded-circle picture-img"
                        />
                        <template v-for="img in data.hotel_info_place_image">
                          <v-lazy-image
                            v-if="img.is_thumbnail"
                            :key="img.id"
                            :src="img.image.replace('upload', 'thumbs200')"
                            alt=""
                            class="rounded-circle picture-img"
                            src-placeholder="../../../assets/images/placeholder.png"
                          />
                        </template>
                        <span class="text-truncate ms-2 d-block w-75">{{
                          data.name
                        }}</span>
                      </div>
                    </td>
                    <td @click="edit(data)">{{ data.slug_name }}</td>
                    <td>
                      <div class="form-check form-switch my-1">
                        <input
                          class="form-check-input"
                          type="checkbox"
                          v-model="data.is_active"
                          @change="
                            changeStatus(data.id, {
                              is_active: data.is_active,
                            })
                          "
                        />
                      </div>
                    </td>
                    <td>
                      <div slot="actions" class="d-flex align-items-center">
                        <i
                          class="fas fa-pencil-alt text-success me-2"
                          title="edit record"
                          @click="edit(data)"
                        ></i>
                        <i
                          class="mdi mdi-trash-can fs-5 text-danger"
                          title="delete record"
                          @click="deleteInfoServices(data.id, index)"
                        >
                        </i>
                        <i
                          v-if="table.tableData.length > 1"
                          class="ms-2 fa fa-align-justify cursor-move d-none"
                          v-b-popover.hover="'Move category'"
                        ></i>
                      </div>
                    </td>
                  </tr>
                </draggable>
                <tbody v-if="!isDataLoading && table.tableData.length == 0">
                  <tr>
                    <td colspan="12" class="text-center">
                      <img
                        src="@/assets/images/empty.svg"
                        class="img-fluid"
                        alt=""
                      />
                      <p>
                        <span class="text-secondary">No Data Found</span>
                      </p>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Drawer is for add and update new hotel info and services -->
    <Drawer
      @close="RoomDetailForm"
      :align="'right'"
      :closeable="true"
      :maskClosable="true"
      :zIndex="1002"
    >
      <div v-if="openRoomDetailDrawer">
        <RoomDetail
          :currentProps="RoomDetailProps"
          @submitted="RoomDetailAddedUpdated"
          @cancelSubmit="RoomDetailForm"
          @saveAndAddNew="saveAndAddNewRoomDetail"
        />
      </div>
    </Drawer>
  </div>
</template>
